import { action, computed, makeObservable, observable, runInAction } from "mobx";
import firebase from 'firebase/compat/app';
import { config } from '../config';
import { fetchWithAuth } from '../app/services/fetchWithAuth';
import { FULL_ADMIN_SCOPE, USER_REQUESTS_SCOPE, hasScope } from './permissions';

// Mirrors the server's isStaffToken so a non-Workspace account lands on
// AccessDenied instead of a UI where every request 403s.
const isStaffToken = (tokenResult) =>
    tokenResult.claims.email_verified === true
    && tokenResult.signInProvider === 'google.com'
    && (tokenResult.claims.email || '').toLowerCase().endsWith('@designwithfriends.com');

const scopesOfClaims = (claims) => {
    if (claims.admin === true) return [FULL_ADMIN_SCOPE];
    return Array.isArray(claims.scopes) ? claims.scopes : [];
};

export class CurrentUserStore {
    @observable user = null;
    @observable.ref scopes = [];
    @observable isLoading = true;

    constructor() {
        makeObservable(this);
        this._unsubscribeAuth = firebase.auth().onAuthStateChanged(this._handleAuthStateChange);
    }

    dispose() {
        this._unsubscribeAuth?.();
    }

    _handleAuthStateChange = async (user) => {
        const scopes = user ? await this._loadScopes(user) : [];
        runInAction(() => {
            this.user = user;
            this.scopes = scopes;
            this.isLoading = false;
        });
    };

    /**
     * The server brings the token's claims in line with the user's row (an
     * invite or a scope change takes effect here), so the token is refreshed
     * right after for the requests that follow.
     */
    async _loadScopes(user) {
        const tokenResult = await user.getIdTokenResult();
        if (!isStaffToken(tokenResult)) return [];
        try {
            const res = await fetchWithAuth(`${config().catalogueServiceBaseUrl}/back-office/admin-users/me/activate`, { method: 'POST' });
            if (res.status === 403) return [];
            if (!res.ok) throw new Error(res.statusText);
            const { scopes } = await res.json();
            await user.getIdToken(true);
            return scopes;
        } catch (e) {
            // catalogue-service being down shouldn't lock out pages served by other backends.
            return scopesOfClaims(tokenResult.claims);
        }
    }

    @action.bound logout = () => {
        firebase.auth().signOut();
    };

    @computed get isAuthenticated() {
        return this.user !== null;
    }

    @computed get hasAccess() {
        return this.scopes.length > 0;
    }

    @computed get isAdmin() {
        return this.scopes.includes(FULL_ADMIN_SCOPE);
    }

    can(scope, ownership = 'mine') {
        return hasScope(this.scopes, scope, ownership);
    }

    /** `can` for one pipeline item: the `.mine` form of a scope covers only the user's own items. */
    canOnItem(scope, item) {
        if (this.can(scope, 'all')) return true;
        if (!this.can(scope) || !item) return false;
        const handlesUserRequests = this.isAdmin || this.scopes.includes(USER_REQUESTS_SCOPE);
        return item.createdByUid === this.user?.uid || (!!item.submittedByUid && handlesUserRequests);
    }
}
